import { z } from "zod";
import { FrameAnnotationCollection, ClientAppType } from "./models.js";
export const DeclineInviteArgs = z.object({
    code: z.string(),
    email: z.string()
});
export const AcceptInviteArgs = z.object({
    code: z.string().min(1),
    name: z.string().min(1),
    email: z.string().email(),
});
export const UpdateMediaArgs = z.object({
    projectId: z.string().min(1),
    id: z.string().min(1),
    // since name is the only thing we can update at the moment,
    // an update request must provide a name
    name: z.string().min(1)
});
export const DeleteMediaArgs = z.object({
    projectId: z.string().min(1),
    id: z.string().min(1)
});
export const CreateTransferFileArgs = z.object({
    name: z.string().min(1),
    size: z.number()
});
export const CreateTransferMeta = z.object({
    ip: z.optional(z.string()),
    countryCode: z.optional(z.string()),
    state: z.optional(z.string()),
    userAgent: z.optional(z.string())
});
export const CreateShareableTransferArgs = z.object({
    name: z.string().min(1),
    provider: z.string().min(1),
    region: z.string().min(1),
    files: CreateTransferFileArgs.array(),
    meta: z.optional(CreateTransferMeta)
});
export const CreateTransferArgs = CreateShareableTransferArgs.extend({
    hidden: z.optional(z.boolean()),
    projectId: z.optional(z.string()),
    mediaId: z.optional(z.string()),
    folderId: z.optional(z.string()),
    accountId: z.string().min(1)
});
export const CreateProjectMediaUploadArgs = z.object({
    projectId: z.string(),
    provider: z.string().min(1),
    region: z.string().min(1),
    /**
     * When media id is provided, the files
     * will be uploaded as versions of an existing
     * media instead of creating new media
     */
    mediaId: z.optional(z.string()),
    /**
     * When folder id is set, the files will
     * be uploaded to this folder inside the project.
     * This field is ignored if `mediaId` is set.
     */
    folderId: z.optional(z.string()),
    files: CreateTransferFileArgs.array(),
    meta: z.optional(CreateTransferMeta)
});
export const CheckUserAuthMethodArgs = z.object({
    email: z.string().email()
});
export const CreateUserArgs = z.object({
    email: z.string().email(),
    name: z.string().min(1),
    password: z.string().min(1)
});
export const CreateAnonymousUserArgs = z.object({
    ip: z.string().min(1).optional(),
    useAgent: z.string().min(1).optional(),
});
export const VerifyUserEmailArgs = z.object({
    code: z.string().min(1),
    email: z.string().email().min(1)
});
export const RequestUserVerificationEmailArgs = z.object({
    email: z.string().email().min(1),
});
export const LoginRequestArgs = z.object({
    email: z.string().email(),
    password: z.string(),
    ip: z.string().optional(),
    countryCode: z.string().optional(),
    userAgent: z.string().optional()
});
export const PasswordResetArgs = VerifyUserEmailArgs.extend({
    password: z.string().min(1)
});
export const LoginWithGoogleRequestArgs = z.object({
    idToken: z.string().min(1),
    ip: z.string().optional(),
    countryCode: z.string().optional(),
    userAgent: z.string().optional(),
    appType: ClientAppType.optional().default("webApp")
});
export const CreateMediaCommentArgs = z.object({
    projectId: z.string().min(1),
    mediaId: z.string().min(1),
    mediaVersionId: z.string().min(1),
    text: z.string().optional(),
    timestamp: z.number().optional(),
    parentId: z.string().optional(),
    annotations: FrameAnnotationCollection.optional(),
});
export const DeleteMediaCommentArgs = z.object({
    projectId: z.string().min(1),
    mediaId: z.string().min(1),
    commentId: z.string().min(1)
});
export const UpdateMediaCommentArgs = z.object({
    projectId: z.string().min(1),
    mediaId: z.string().min(1),
    commentId: z.string().min(1),
    text: z.string().min(1)
});
export const SendEmailAnnouncementArgs = z.object({
    password: z.string().min(1),
    message: z.string().min(1),
    subject: z.string().min(1),
});
export const InitTransferFileUploadArgs = z.object({
    transferId: z.string().min(1),
    fileId: z.string().min(1),
    blockSize: z.number()
});
export const S3InitFileUploadResult = z.object({
    uploadId: z.string().optional(),
    blockSize: z.number(),
    size: z.number(),
    blocks: z.object({
        index: z.number(),
        size: z.number(),
        url: z.string(),
    }).array()
});
export const CompleteFileUploadArgs = z.object({
    transferId: z.string().min(1),
    fileId: z.string().min(1),
    providerArgs: z.any()
});
export const S3CompleteFileUploadProviderArgs = z.object({
    uploadId: z.string(),
    blocks: z.object({
        index: z.number(),
        etag: z.string()
    }).array()
});
export const S3CompleteFileUploadResult = z.object({
    etag: z.string()
});
export const UpdateProjectArgs = z.object({
    id: z.string().min(1),
    name: z.string().min(1)
});
export const DeleteProjectArgs = z.object({
    id: z.string().min(1)
});
export const ChangeProjectMemberRoleArgs = z.object({
    projectId: z.string().min(1),
    userId: z.string().min(1),
    role: z.enum(["reviewer", "editor", "admin"])
});
export const RemoveProjectMemberArgs = z.object({
    projectId: z.string().min(1),
    userId: z.string().min(1)
});
export const CreateFolderArgs = z.object({
    projectId: z.string().min(1),
    name: z.string().min(1),
    parentId: z.optional(z.string().min(1))
});
export const CreateFolderTreeArgs = z.object({
    projectId: z.string().min(1),
    paths: z.array(z.string().min(1)),
});
export const GetProjectItemsArgs = z.object({
    projectId: z.string().min(1),
    folderId: z.optional(z.string().min(1))
});
export const UpdateFolderArgs = z.object({
    projectId: z.string().min(1),
    id: z.string().min(1),
    // Right now the name is required cause it's the only
    // thing that can be updated.
    // If we have other properties, we can make this optional
    name: z.string().min(1)
});
export const FinalizeTransferArgs = z.object({
    transferId: z.string().min(1),
    duration: z.number().gte(0),
    recovered: z.optional(z.boolean())
});
export const MoveMediaToFolderArgs = z.object({
    projectId: z.string().min(1),
    targetFolderId: z.union([z.string().min(1), z.null()]),
    mediaIds: z.array(z.string().min(1)).min(1),
});
export const MoveFoldersToFolderArgs = z.object({
    projectId: z.string().min(1),
    targetFolderId: z.union([z.string().min(1), z.null()]),
    folderIds: z.array(z.string().min(1)).min(1)
});
export const SearchProjectFolderArgs = z.object({
    projectId: z.string().min(1),
    searchTerm: z.string()
});
export const MoveProjectItemsToFolderArgs = z.object({
    projectId: z.string().min(1),
    items: z.array(z.object({
        id: z.string().min(1),
        type: z.enum(['folder', 'media'])
    })).min(1),
    targetFolderId: z.union([z.string().min(1), z.null()]),
});
export const DeleteProjectItemsArgs = z.object({
    projectId: z.string().min(1),
    items: z.array(z.object({
        id: z.string().min(1),
        type: z.enum(['folder', 'media'])
    })).min(1)
});
export const GetProjectMediaByIdArgs = z.object({
    projectId: z.string().min(1),
    mediaId: z.string().min(1)
});
export const InviteUsersToProjectArgs = z.object({
    projectId: z.string().min(1),
    users: z.array(z.object({
        email: z.string().min(1)
    })).min(1),
    role: z.enum(['editor', 'admin', 'reviewer']),
    message: z.string().optional()
});
export const CreateProjectShareArgs = z.object({
    name: z.string().min(1),
    projectId: z.string().min(1),
    allowDownload: z.boolean().optional(),
    showAllVersions: z.boolean().optional(),
    allowComments: z.boolean().optional(),
    password: z.optional(z.string().min(1)),
    public: z.boolean(),
    allItems: z.optional(z.boolean()),
    items: z.array(z.object({
        _id: z.string().min(1),
        type: z.enum(['folder', 'media'])
    })),
    expiresAt: z.date().optional(),
    recipients: z.array(z.object({
        email: z.string().min(1)
    })),
});
export const UpdateProjectShareArgs = z.object({
    projectId: z.string().min(1),
    shareId: z.string().min(1),
    name: z.string().optional(),
    enabled: z.boolean().optional(),
    public: z.boolean().optional(),
    // if has password is set to true and password is null,
    // then the share will retain its current password if it has one,
    // otherwise password is not set.
    // If hasPassword is false, the current password will be removed
    // if one is set.
    hasPassword: z.boolean().optional(),
    password: z.string().min(1).optional(),
    expiresAt: z.date().optional(),
    allowComments: z.boolean().optional(),
    showAllVersions: z.boolean().optional(),
    allowDownload: z.boolean().optional(),
    recipients: z.array(z.object({
        email: z.string().min(1)
    })).optional()
});
export const DeleteProjetShareArgs = z.object({
    projectId: z.string().min(1),
    shareId: z.string().min(1)
});
export const GetProjectShareLinkItemsArgs = z.object({
    shareId: z.string().min(1),
    code: z.string().min(1),
    password: z.string().min(1).optional(),
    folderId: z.string().min(1).optional()
});
export const GetProjectShareMediaByIdArgs = z.object({
    shareId: z.string().min(1),
    shareCode: z.string().min(1),
    password: z.string().min(1).optional(),
    mediaId: z.string(),
});
export const CreateProjectShareMediaCommentArgs = z.object({
    authorName: z.string().min(1).optional(),
    shareId: z.string().min(1),
    shareCode: z.string().min(1),
    password: z.string().min(1).optional(),
    mediaId: z.string().min(1),
    mediaVersionId: z.string().min(1),
    text: z.string().optional(),
    timestamp: z.number().optional(),
    parentId: z.string().optional(),
    annotations: FrameAnnotationCollection.optional(),
});
export const UpdateProjectShareMediaCommentArgs = z.object({
    shareId: z.string().min(1),
    shareCode: z.string().min(1),
    password: z.string().min(1).optional(),
    mediaId: z.string().min(1),
    commentId: z.string().min(1),
    text: z.string().min(1)
});
export const DeleteProjectShareMediaCommentArgs = z.object({
    shareId: z.string().min(1),
    shareCode: z.string().min(1),
    password: z.string().min(1).optional(),
    mediaId: z.string().min(1),
    commentId: z.string().min(1)
});
export const GetAllProjectShareFilesForDownloadArgs = z.object({
    shareId: z.string().min(1),
    shareCode: z.string().min(1),
    password: z.string().min(1).optional()
});
export const UpdateMediaVersionsArgs = z.object({
    projectId: z.string().min(1),
    mediaId: z.string().min(1),
    preferredVersionId: z.string().min(1).optional(),
    // this updates the versions array, it's used
    // to rename, re-order or delete versions.
    // It essentially replaces the current version array.
    // As a result, we use the concurrency check to ensure
    // no concurrent updates occur. The update will only succeed
    // if the version that the client has is the most recent
    versions: z.array(z.object({
        _id: z.string().min(1),
        name: z.string().min(1)
    })).optional(),
    // use to make sure we have the latest version of the record
    // before we make an update
    concurrencyControl: z.number().optional()
});
export const LegacyDownloadRequestArgs = z.object({
    transferId: z.string().min(1),
    ip: z.string().optional(),
    countryCode: z.string().optional(),
    userAgent: z.string().optional(),
});
export const GetAccountUsageArgs = z.object({
    accountId: z.string().min(1)
});
export const InitiateSubscrptionArgs = z.object({
    plan: z.string().min(1),
    // one-off, non-renewing subscription
    oneOff: z.boolean().optional(),
    ip: z.string().optional(),
});
export const InitiateManualSubscriptionArgs = z.object({
    plan: z.string().min(1),
    accountId: z.string().min(1),
    // 2-letter uppercase country code, used to determine pricing and currenc
    region: z.string().optional(),
    metadata: z.object({
        paymentMethod: z.string().min(1),
        // unique string, if not payment was made, then create random unique string
        // will be used as providerId
        reference: z.string().min(1),
        // object with arbitrary keys
        details: z.any(),
        // reason for using manual method
        reason: z.string().min(1)
    }),
    adminAuth: z.string().min(1),
});
export const AdminVerifyTransactionArgs = z.object({
    transactionId: z.string().min(1),
    adminAuth: z.string().min(1)
});
export const InitiateSubscriptionForAnonymousUserArgs = z.object({
    plan: z.string().min(1),
    oneOff: z.boolean().optional(),
    ip: z.string().optional(),
    userAgent: z.string().optional()
});
export const InitiateCreditsPurchaseArgs = z.object({
    price: z.number(),
    currency: z.string(),
    ip: z.string().optional(),
    userAgent: z.string().optional()
});
;
export const InitiateCreditsPurchaseForAnonymousUserArgs = z.object({
    price: z.number(),
    currency: z.string(),
    ip: z.string().optional(),
    userAgent: z.string().optional()
});
export const GetProjectActivityArgs = z.object({
    projectId: z.string().min(1),
    from: z.date().optional(),
    to: z.date().optional()
});
export const GetProjectTransferArgs = z.object({
    projectId: z.string().min(1),
    transferId: z.string().min(1)
});
export const GetProjectFilesForDownloadArgs = z.object({
    projectId: z.string().min(1),
    items: z.array(z.object({
        type: z.enum(['folder', 'media']),
        _id: z.string().min(1)
    })).nonempty(),
});
export const CreateProjectForAnonymousUserArgs = z.object({
    userAgent: z.string().min(1).optional(),
    ip: z.string().optional()
});
export const UpdateUserMetaArgs = z.object({
    goalSelection: z.object({
        collaborate: z.boolean(),
        storage: z.boolean(),
        share: z.boolean(),
        other: z.boolean(),
        otherDetails: z.string().optional()
    }).optional(),
    industrySelection: z.object({
        film: z.boolean(),
        marketing: z.boolean(),
        contentCreator: z.boolean(),
        photography: z.boolean(),
        other: z.boolean(),
        otherDetails: z.string().optional()
    }).optional(),
});
export const UpdateWhatsAppNumberArgs = z.object({
    number: z.string().min(1).regex(/^\+\d+$/)
});
export const VerifyWhatsAppNumberArgs = z.object({
    verificationRequestId: z.string().min(1),
    code: z.string().min(1),
});
//# sourceMappingURL=dtos.js.map